
import { Component, Vue } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";

import BufferSeek from "./media/BufferSeek.vue";
import AuditVideo from "./AuditVideo.vue";
import { MediaFile, AuditTimeStamp, AuditService } from "@/services/auditService";
import axios, { CancelTokenSource } from "axios";
import { UserPermissions } from "@/store/types";
import { SessionResource } from "@/store/sessions/types";
import { EventRecord } from '@/store/site-monitor/types';
import { AuditRecordDTO } from "@/store/site-monitor/types";
import EventRecordTypes from "@/types/sv-data/enums/EventRecordTypes";

const SessionStore = namespace("sessions");
const SiteMonitor = namespace("siteMonitor");

@Component({
	components: {
		"audit-video": AuditVideo,
		"buffer-seek": BufferSeek
	}
})
export default class AuditControl extends Vue {
	$refs!: {
		video: AuditVideo;
	};

	@SiteMonitor.Action createAuditRecord: (request: AuditRecordDTO) => Promise<EventRecord>;

	@Getter("getUserName") public currentUserName!: string;

	@SessionStore.Getter private getSession: (resourceId: number) => string;
	@Getter private getFeature: (featureName: string[]) => boolean;
	@Getter getPermissions: UserPermissions;

	public currentFile: MediaFile | null = null;

	private auditService: AuditService | null = null;
	private eventRecordID: number | null = null;
	private eventID: number | null = null;

	private playing: boolean = false;
	private ended: boolean = false;
	private playBackSpeed: number = 1.0;

	private duration: AuditTimeStamp = {
		time: 0,
		minutes: 0,
		seconds: 0,
		totalSeconds: 0
	};

	private timestamp: AuditTimeStamp = {
		time: 0,
		minutes: 0,
		seconds: 0,
		totalSeconds: 0
	};

	private dataSize: number = 0;
	private appendLock: boolean = false;
	private appendTimeout: NodeJS.Timeout = null;
	private restartTimeout: NodeJS.Timeout = null;
	private restartErrorCount: number = 0;
	private restartIsSpinning: boolean = false;

	private moreDataAvailable: boolean = false;
	private latestClipLength: number | null = null;

	private isMuted: boolean = true;
	private mediaHasAudio: boolean = false;
	private mediaHasVideo: boolean = false;
	private appendDataCancellationTokenSource: CancelTokenSource;

	public async destroyed(): Promise<void>{
		clearTimeout(this.appendTimeout);
		clearTimeout(this.restartTimeout);
		this.restartErrorCount = 0;
		this.restartIsSpinning = false;
		this.stop();
	}

	/**
	 * Stops current playback, stops fetching data, resets the control and begins playing a new clip.
	 */
	public async playClip(auditService: AuditService, eventRecordID: number, clip: any, eventID: number = -1, isRestart: boolean = false) {
		this.stop(isRestart);

		this.auditService = auditService;
		this.eventRecordID = eventRecordID;
		this.eventID = eventID;
		this.currentFile = clip;
		this.ended = false;
		this.playBackSpeed = 1.0;

		await this.$nextTick();

		this.playing = true;
		this.$refs.video.open();
		this.appendVideo();
	}

	/**
	 * Clears our append timeout and restart timeout, and resets the state of the video control.
	 */
	public stop(isRestart: boolean = false) {
		clearTimeout(this.appendTimeout);
		clearTimeout(this.restartTimeout);
		this.restartIsSpinning = false;

		if (this.appendDataCancellationTokenSource) {
			this.appendDataCancellationTokenSource.cancel("Stopping");
		}

		if (this.$refs.video) {
			this.$refs.video.close();
		}

		this.moreDataAvailable = true;
		this.dataSize = 0;
		this.appendLock = false;
		this.latestClipLength = null;
		this.ended = true;
		this.playing = false;

		if (!isRestart)
		{
			this.restartErrorCount = 0;
		}

		this.$set(this, "duration", {
			time: 0,
			minutes: 0,
			seconds: 0,
			totalSeconds: 0
		});

		this.$set(this, "timestamp", {
			time: 0,
			minutes: 0,
			seconds: 0,
			totalSeconds: 0
		});

		this.eventID = null;
		this.eventRecordID = null;
		this.currentFile = null;
	}

	/**
	 * Attempts to fetch new video data from the audit service, and if any is returned, appends it to the audit clip within our
	 * video player.
	 *
	 * TODO: This really should be part of the Audit Video component, or the clip. The use of $refs here is icky.
	 */
	private async appendVideo() {
		if (this.ended || this.appendLock || !this.auditService) {
			return;
		}

		this.appendLock = true;

		// Stop any further video data requests until this one is finished.
		clearTimeout(this.appendTimeout);

		// Get our audit service url.
		const auth = this.getSession(SessionResource.AuditServiceSession);
		const mediaUrl = this.auditService.mediaUrl(
			auth,
			this.eventRecordID!,
			this.currentFile!.FileIdentifier,
		);

		try {
			if (this.appendDataCancellationTokenSource) {
				this.appendDataCancellationTokenSource.cancel("Cancelling other requests");
			}

			this.appendDataCancellationTokenSource = axios.CancelToken.source();

			// Fetch the next packet of video data from the audit service.
			const { headers, data } = await this.auditService.auditDataGet(mediaUrl, this.dataSize, this.appendDataCancellationTokenSource.token);
			const contentRange: string = headers["content-range"];

			if (this.ended) {
				this.appendLock = false;
				return;
			}

			if (contentRange) {
				// If audit returned a content range, extract stream sizes.
				const [rangeStr, maxSizeStr] = contentRange.split("/"); // x-y/z -> [x-y, z]
				const [rangeMinStr, rangeMaxStr] = rangeStr.split("-"); // x-y -> [x, y]

				// size = the end offset of this data packet range.
				const size = parseInt(rangeMaxStr);

				// maxSize = the maximum size of the video stream.
				const maxSize = parseInt(maxSizeStr);

				// Update our latest clip length for the seek bar.
				this.latestClipLength = maxSize;

				if (this.$refs.video) {
					if (data && data.byteLength > 0) {
						// If we've got data, add it to the video.
						this.dataSize = size;
						this.$refs.video.addVideoData(data);
						this.moreDataAvailable = this.latestClipLength > size;
					} else {
						this.$refs.video.processRemainingData();
						this.moreDataAvailable = false;
					}
				}
			}

			// Setup next timeout to fetch video data.
			if (this.appendTimeout) {
				clearTimeout(this.appendTimeout);
			}

			this.appendTimeout = setTimeout(
				() => {
					this.appendVideo();
				},
				this.moreDataAvailable ? 100 : 2000
			);

		} catch (err) {
			if (axios.isCancel(err)) {
				console.error(`AuditControl [EventID: ${this.eventID} - EventRecordID: ${this.eventRecordID}] - GetData request cancelled ${err}`);
				return;
			}

			console.error(`AuditControl [EventID: ${this.eventID} - EventRecordID: ${this.eventRecordID}] - Error fetching audit data: ${err}`);
			this.moreDataAvailable = false;
		} finally {
			this.appendLock = false;
		}
	}

	/**
	 * Handler for when the video player raises an ended event.
	 */
	private onVideoEnded() {
		try {
			if (this.playing) {
				if (this.moreDataAvailable) {
					// Wait - we don't have the full clip yet ...
				} else {
					this.$refs.video.seek(0);
					this.$refs.video.play();
				}
			}
		} catch (err) {
			console.error(`AuditControl [EventID: ${this.eventID} - EventRecordID: ${this.eventRecordID}] - Error: ${err}`);
		}
	}

	/**
	 * Handler for when the audit video component throws an error. If required, we'll
	 * attempt to restart the stream.
	 */
	private onVideoError({ restart }: { restart: boolean }): void {
		clearTimeout(this.appendTimeout);

		if (restart) {
			clearTimeout(this.restartTimeout);

			this.restartErrorCount += 1;

			if (this.restartErrorCount > this.maxErrorRestartAttempts) {
				console.log(`onVideoError raised, Max number of restarts attempted. [EventID: ${this.eventID} - EventRecordID: ${this.eventRecordID}]`);
				return;
			}

			console.log(`onVideoError raised, restarting audit client in ${this.errorRestartTimeoutInSeconds}s. [EventID: ${this.eventID} - EventRecordID: ${this.eventRecordID}]`);

			this.restartTimeout = setTimeout((clip: MediaFile, eventRecordID: number, eventID: number) => {
				this.restartIsSpinning = false;

				console.log(`onVideoError - Actioning audit client restart (Attempt: ${this.restartErrorCount}). [EventID: ${this.eventID} - EventRecordID: ${this.eventRecordID}]`);
				const isRestart = true;
				this.playClip(this.auditService, eventRecordID, clip, eventID, isRestart);
			}, this.errorRestartTimeoutInSeconds * 1000, this.currentFile, this.eventRecordID, this.eventID);
		}
	}

	private tryRestart(): void {
		this.restartIsSpinning = true;

		console.log(`AuditControl [EventID: ${this.eventID} - EventRecordID: ${this.eventRecordID}] - Force Restart - Actioning audit client restart.`);
		this.playClip(this.auditService, this.eventRecordID, this.currentFile, this.eventID);
	}

	private get maxErrorRestartAttempts(): number
	{
		return 5;
	}

	/**
	 * Gets the timeout in seconds for restarting the video.
	 */
	private get errorRestartTimeoutInSeconds(): number {
		return 5 * this.restartErrorCount;
	}

	/**
	 * Handler for when the current clip duration is updated.
	 */
	private onClipDurationChanged(duration: AuditTimeStamp) {
		this.$set(this, "duration", duration);
	}

	/**
	 * Handler for when the current clip time is updated.
	 */
	private onClipTimeChanged(timestamp: AuditTimeStamp) {
		this.$set(this, "timestamp", timestamp);
	}

	/**
	 * Handler for when the user updates the video seek bar.
	 */
	private onSliderSeek(seekTo: number) {
		this.$refs.video.seek(seekTo);
	}

	/**
	 * Toggles playback of the video control.
	 */
	private togglePlaying() {
		if (this.playing) {
			this.playing = false;
			this.$refs.video.pause();
		} else {
			this.playing = true;
			this.$refs.video.play();
		}
	}

	/**
	 * Increases playback speed of the video control.
	 */
	private speedUp() {
		if (this.playBackSpeed + 0.5 <= 5.0) {
			this.playBackSpeed += 0.5;
			this.$refs.video.setPlaybackSpeed(this.playBackSpeed);
		}
	}

	/**
	 * Decreases playback speed of the video control.
	 */
	private slowDown() {
		if (this.playBackSpeed - 0.5 >= 0.5) {
			this.playBackSpeed -= 0.5;
			this.$refs.video.setPlaybackSpeed(this.playBackSpeed);
		}
	}

	private get canUnmute():boolean{
		return this.getFeature(["Alarms", "MediaMatrix", "UnmuteAudio"]);
	}

	private get cameraSnapshotFeatureEnabled(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "SnapShot"]);
	}

	private get cameraSnapshotPermissionEnabled(): boolean {
		return this.getPermissions.canPerformMatrixSnapshot;
	}

	private get cameraSnapshotEnabled(): boolean {
		return this.cameraSnapshotFeatureEnabled && this.cameraSnapshotPermissionEnabled;
	}

	muteUnMute(): void {
		this.isMuted = !this.isMuted;
		this.$refs.video.muteUnMute(this.isMuted);
	}

	private onMediaHasAudio(mediaHasAudio): void {
		this.mediaHasAudio = mediaHasAudio;
	}
	
	private onMediaHasVideo(mediaHasVideo): void {
		this.mediaHasVideo = mediaHasVideo;
	}

	private async takeSnapShot(): Promise<void> {
		const imageTimestamp =
			this.timestamp.minutes.toString().padStart(2, "0") + this.timestamp.seconds.toString().padStart(2, "0");
		const ImageURL = this.$refs.video.currentImage();

		let link = document.createElement("a");
		link.href = ImageURL;

		const dateTime = new Date();
		dateTime.setTime(dateTime.getTime() - dateTime.getTimezoneOffset() * 60 * 1000);

		const dateTimeString =
			dateTime.getUTCFullYear().toString() +
			"-" +
			(dateTime.getUTCMonth() + 1).toString() +
			"-" +
			dateTime.getUTCDate().toString() +
			"_" +
			dateTime.getUTCHours().toString() +
			"-" +
			dateTime.getUTCMinutes().toString() +
			"-" +
			dateTime.getUTCSeconds().toString();

		let clipFilename = "Snapshot_";
		clipFilename = clipFilename + this.eventID + "_" + dateTimeString + "_" + imageTimestamp + ".jpg";
		link.download = clipFilename;

		link.click();

		await this.auditSnapshot(ImageURL, clipFilename);
	}

	private async auditSnapshot(snapshotImage: string, fileName: string): Promise<void> {
		const eventRecord = await this.createAuditRecord({
			eventId: this.eventID,
			eventRecordTypeId: EventRecordTypes.RecordedVideoSnapShot,
			details: `User ${this.currentUserName} took snapshot of a clip - ${fileName}`,
			objectId: this.eventRecordID
		});

		const auth = this.getSession(SessionResource.AuditServiceSession);
		const imageBlob = await (await fetch(snapshotImage)).blob();

		await this.auditService.attachMediaDataToEventRecord(
			auth,
			eventRecord.eventID,
			eventRecord.eventRecordID,
			this.currentFile.DeviceId,
			null,
			imageBlob,
			fileName
		);
	}
}
